/**
 * Pure ask helpers shared by the Inbox and the service worker (no app/Api imports: the worker
 * bundle must stay free of page-only code).
 */
import type { EncryptedContent, PrivateNotification, TranscriptEvent } from '@viko/protocol'
import type { AskEvent } from './types.js'

export const LOCKED_TITLE = 'Encrypted: unlock this browser to read'

/** Decrypts one authenticated ask event; throws if locked, forged or for another account. */
export type AskOpener = (event: EncryptedContent) => Promise<TranscriptEvent>

/** Question / approval text as shown in the Inbox card and (locally built) in a notification. */
export function describeAsk(e: AskEvent): { title: string; body: string } {
  if (e.kind === 'question') {
    const first = e.questions[0]
    const more = e.questions.length - 1
    return {
      title: first?.header?.trim() || 'Question',
      body: `${first?.question ?? ''}${more > 0 ? ` (+${more} more)` : ''}`.trim(),
    }
  }
  const what = e.title ?? e.command ?? e.toolName ?? `Approve ${e.approvalKind}`
  return { title: 'Approval needed', body: String(what) }
}

const isAsk = (e: TranscriptEvent): e is AskEvent => e.kind === 'question' || e.kind === 'approval'

/** Open one private ask; the decrypted event must be the exact event the hub pointed at. */
export async function openPrivateAsk(
  n: Pick<PrivateNotification, 'kind' | 'runId' | 'seq' | 'event'>,
  open: AskOpener,
): Promise<AskEvent> {
  const c = n.event.context
  if (c.runId !== n.runId || c.seq !== n.seq || c.kind !== n.kind)
    throw new Error('Ask coordinates mismatch')
  const event = await open(n.event)
  if (!isAsk(event) || event.kind !== n.kind || event.seq !== n.seq || event.runId !== n.runId)
    throw new Error('Ask envelope mismatch')
  return event
}
